// src/runtime/KnowledgeEvolutionRuntime/types.ts
// ============================================
// Knowledge Evolution Runtime 类型定义（第十一个 Runtime）
//
// 依据：《XEAOS 企业操作系统白皮书》第十五章《Knowledge Evolution Protocol（知识演化协议）》
//
// 核心概念：
//   1. CandidateKnowledge（候选知识）：从各种来源自动提取的知识条目
//   2. Knowledge Inbox（候选知识暂存区）：30_Inbox/，全自动写入
//   3. RiskLevel（风险等级）：低/中/高三档，决定处理路径
//      - 低风险 → 自动通过，直接写入正式知识库
//      - 中风险 → 标记待确认，CEO 可一键确认/拒绝
//      - 高风险 → 必须 CEO 确认，未确认不入库
//   4. DailyKnowledgeReport（"今天公司学到了什么"日报）：每天 23:00 自动生成
//
// 知识提取来源：
//   Hermes 对话 / Runtime 日志 / 审核反馈 / Git Commit / 客户跟进记录
// ============================================

// ============================================
// 知识来源类型
// ============================================

export enum KnowledgeSourceType {
  /** Hermes 对话记录 */
  HermesDialogue = 'hermes_dialogue',
  /** Runtime 执行日志 */
  RuntimeLog = 'runtime_log',
  /** 内容审核反馈 */
  ReviewFeedback = 'review_feedback',
  /** Git Commit 提交记录 */
  GitCommit = 'git_commit',
  /** 客户跟进记录 */
  CustomerFollowUp = 'customer_follow_up',
}

/** 所有知识来源（用于统计与筛选） */
export const ALL_KNOWLEDGE_SOURCES: KnowledgeSourceType[] = [
  KnowledgeSourceType.HermesDialogue,
  KnowledgeSourceType.RuntimeLog,
  KnowledgeSourceType.ReviewFeedback,
  KnowledgeSourceType.GitCommit,
  KnowledgeSourceType.CustomerFollowUp,
];

/** 来源中文名 */
export const KNOWLEDGE_SOURCE_LABEL: Record<KnowledgeSourceType, string> = {
  [KnowledgeSourceType.HermesDialogue]: 'Hermes 对话',
  [KnowledgeSourceType.RuntimeLog]: 'Runtime 日志',
  [KnowledgeSourceType.ReviewFeedback]: '审核反馈',
  [KnowledgeSourceType.GitCommit]: 'Git Commit',
  [KnowledgeSourceType.CustomerFollowUp]: '客户跟进记录',
};

// ============================================
// 候选知识风险等级
// ============================================

export type CandidateRiskLevel = 'low' | 'medium' | 'high';

/** 风险等级中文名 */
export const RISK_LEVEL_LABEL: Record<CandidateRiskLevel, string> = {
  low: '低风险',
  medium: '中风险',
  high: '高风险',
};

/** 风险等级说明 */
export const RISK_LEVEL_DESC: Record<CandidateRiskLevel, string> = {
  low: '内部操作性知识，不涉及对外承诺与财务，自动通过入库',
  medium: '涉及客户/产品/对外表述，需要 CEO 确认后再入库',
  high: '涉及战略/财务/对外承诺/法规，必须 CEO 确认后才能入库',
};

// ============================================
// 候选知识状态
// ============================================

export type CandidateStatus =
  | 'pending' // 已提取，待评估
  | 'auto_approved' // 低风险自动通过（已写入正式知识库）
  | 'needs_review' // 中风险待确认
  | 'needs_ceo' // 高风险必须 CEO 确认
  | 'approved' // CEO 确认入库
  | 'rejected'; // CEO 拒绝归档

export const CANDIDATE_STATUS_LABEL: Record<CandidateStatus, string> = {
  pending: '待评估',
  auto_approved: '低风险自动通过',
  needs_review: '待 CEO 确认',
  needs_ceo: '必须 CEO 确认',
  approved: '已确认入库',
  rejected: '已拒绝归档',
};

// ============================================
// 候选知识条目
// ============================================

export interface CandidateKnowledge {
  /** 候选知识 ID（文件名 slug） */
  id: string;
  /** 标题 */
  title: string;
  /** 摘要（一句话） */
  summary: string;
  /** 正文（完整内容） */
  content: string;
  /** 来源类型 */
  source: KnowledgeSourceType;
  /** 来源引用（对话 ID / 日志路径 / commit hash / 客户 ID） */
  sourceRef: string;
  /** 风险等级 */
  riskLevel: CandidateRiskLevel;
  /** 状态 */
  status: CandidateStatus;
  /** 提取时间（ISO 8601） */
  detectedAt: string;
  /** 处理时间（确认/拒绝/入库） */
  processedAt?: string;
  /** 目标正式库分类（如 Agent / CRM / Marketing / LLM / Quant） */
  targetCategory: string;
  /** 标签 */
  tags: string[];
  /** 价值评分 0-100 */
  score: number;
  /** CEO 处理备注（拒绝原因 / 修改意见） */
  feedback?: string;
}

// ============================================
// "今天公司学到了什么"日报
// ============================================

export interface DailyKnowledgeReport {
  /** 日期 YYYY-MM-DD */
  date: string;
  /** 生成时间 */
  generatedAt: string;
  /** 当日新增候选知识数 */
  newCandidates: number;
  /** 自动通过数 */
  autoApproved: number;
  /** 待确认数（中风险） */
  needsReview: number;
  /** 必须确认数（高风险） */
  needsCeo: number;
  /** 今日洞察列表 */
  insights: Array<{
    title: string;
    summary: string;
    source: KnowledgeSourceType;
  }>;
  /** 落盘路径 */
  vaultPath: string;
}

// ============================================
// 统计
// ============================================

export interface KnowledgeEvolutionStats {
  /** 累计候选知识数 */
  totalCandidates: number;
  /** 按状态统计 */
  byStatus: Partial<Record<CandidateStatus, number>>;
  /** 按风险统计 */
  byRisk: Partial<Record<CandidateRiskLevel, number>>;
  /** 按来源统计 */
  bySource: Partial<Record<KnowledgeSourceType, number>>;
  /** 上次运行时间 */
  lastRunAt?: string;
  /** 最近一期日报 */
  lastReport?: DailyKnowledgeReport;
}

// ============================================
// 查询 / 命令枚举
// ============================================

export enum KnowledgeEvolutionQuery {
  /** 列出候选知识（可按状态/风险/来源/日期筛选） */
  ListCandidates = 'list_candidates',
  /** 获取单个候选知识 */
  GetCandidate = 'get_candidate',
  /** 获取统计概览 */
  GetStats = 'get_stats',
  /** 获取指定日期日报 */
  GetDailyReport = 'get_daily_report',
  /** 列出所有日报 */
  ListReports = 'list_reports',
}

export enum KnowledgeEvolutionCommand {
  /** 运行一次完整的知识演化流程（提取 → 评估 → 整理 → 入库 → 日报） */
  RunDailyEvolution = 'run_daily_evolution',
  /** 立即提取候选知识（不整理不入库） */
  ExtractNow = 'extract_now',
  /** CEO 确认候选知识入库 */
  ConfirmCandidate = 'confirm_candidate',
  /** CEO 拒绝候选知识归档 */
  RejectCandidate = 'reject_candidate',
  /** 重置运行时状态（保留 Vault 已落盘内容） */
  Reset = 'reset',
}

// ============================================
// 统一响应类型
// ============================================

export interface KnowledgeEvolutionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}
